import type { ResolvedApp, ResolvedScreen } from "./index-types";
import { normalizePath } from "./StudioScreenRenderer.utils.path";
import { extractLocalePrefix } from "./StudioScreenRenderer.utils.locale-route";
import { extractRouteParamsFromTemplate } from "./StudioScreenRenderer.utils.route-params";

function getScreenById(
  app: ResolvedApp,
  screenId: string | undefined,
): ResolvedScreen | null {
  if (!screenId) return null;
  return app.screens[screenId] ?? null;
}

/** Mirror the server's route order in the client renderer. */
export function resolveScreenForPath(
  app: ResolvedApp,
  path: string,
): ResolvedScreen | null {
  // The locale prefix (/en, /es, /ar, ...) selects the page language but is
  // not part of screen matching — strip it before resolving the route.
  const strippedPath =
    extractLocalePrefix(path, app.i18n?.locales ?? [])?.strippedPath ??
    String(path ?? "");
  const normalizedPath = normalizePath(strippedPath);
  const exactRouteScreen = getScreenById(app, app.routeIndex?.[normalizedPath]);
  if (exactRouteScreen) return exactRouteScreen;

  for (const [routeSlug, routeTemplate] of Object.entries(app.config.routes ?? {})) {
    if (typeof routeTemplate !== "string") continue;
    if (extractRouteParamsFromTemplate(routeTemplate, normalizedPath) === null) continue;

    const mappedScreenId = app.slugIndex[routeSlug] ??
      (app.screens[routeSlug] ? routeSlug : undefined);
    const matchedScreen = getScreenById(app, mappedScreenId);
    if (matchedScreen) return matchedScreen;
  }

  const directScreenId = app.slugIndex[normalizedPath] ??
    (app.screens[normalizedPath] ? normalizedPath : undefined);
  const directScreen = getScreenById(app, directScreenId);
  if (directScreen) return directScreen;

  const firstSegment = normalizedPath.split("/").filter(Boolean)[0];
  const fallbackScreenId = firstSegment
    ? app.slugIndex[firstSegment] ??
      (app.screens[firstSegment] ? firstSegment : undefined)
    : app.config.entrypoints?.initial ?? app.config.entry;
  return getScreenById(app, fallbackScreenId);
}